import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff, RefreshCw } from 'lucide-react';

export default function ResetPasswordPage({ API }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [success, setSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenError('No reset token provided');
        setVerifying(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        if (res.data.valid) {
          setTokenValid(true);
        } else {
          setTokenError(res.data.reason || 'Invalid token');
        }
      } catch (error) {
        setTokenError('Failed to verify token');
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token, API]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          backgroundColor: '#1E3A4C',
          backgroundImage: `url('/sfl-bg.png')`,
          backgroundRepeat: 'repeat',
          backgroundSize: '200px 200px'
        }}
      >
        <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl border-0">
          <CardContent className="py-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-[#FF7F50] mx-auto mb-4" />
            <p className="text-gray-600">Verifying reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid/expired token state
  if (!tokenValid && !success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          backgroundColor: '#1E3A4C',
          backgroundImage: `url('/sfl-bg.png')`,
          backgroundRepeat: 'repeat',
          backgroundSize: '200px 200px'
        }}
      >
        <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-[#1E3A4C]">
              Invalid Reset Link
            </CardTitle>
            <CardDescription className="text-gray-600">
              {tokenError || 'This password reset link is invalid or has expired.'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4 space-y-4">
            <Link to="/forgot-password" className="block">
              <Button className="w-full h-12 bg-[#FF7F50] hover:bg-[#ff6b3d] text-white font-semibold">
                Request New Reset Link
              </Button>
            </Link>
            
            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full text-[#1E3A4C]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          backgroundColor: '#1E3A4C',
          backgroundImage: `url('/sfl-bg.png')`,
          backgroundRepeat: 'repeat',
          backgroundSize: '200px 200px'
        }}
      >
        <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-[#1E3A4C]">
              Password Reset!
            </CardTitle>
            <CardDescription className="text-gray-600">
              Your password has been successfully reset. You can now log in with your new password.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            <Button 
              onClick={() => navigate('/login')}
              className="w-full h-12 bg-[#FF7F50] hover:bg-[#ff6b3d] text-white font-semibold"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset form
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundColor: '#1E3A4C',
        backgroundImage: `url('/sfl-bg.png')`,
        backgroundRepeat: 'repeat',
        backgroundSize: '200px 200px'
      }}
    >
      <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="inline-block mb-4">
            <img src="/logo-new.png" alt="Survivor Fantasy League" className="h-16 mx-auto" />
          </Link>
          <CardTitle className="text-2xl font-bold text-[#1E3A4C]">
            Reset Your Password
          </CardTitle>
          <CardDescription className="text-gray-600">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 border-gray-300 focus:border-[#FF7F50] focus:ring-[#FF7F50]"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">Must be at least 8 characters</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-700">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 h-12 border-gray-300 focus:border-[#FF7F50] focus:ring-[#FF7F50]"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#FF7F50] hover:bg-[#ff6b3d] text-white font-semibold"
              data-testid="reset-password-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Resetting...
                </span>
              ) : (
                'Reset Password'
              )}
            </Button>
            
            <div className="text-center pt-2">
              <Link 
                to="/login" 
                className="text-[#4682B4] hover:text-[#1E3A4C] font-medium inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
